import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Flex, Text, Spinner, Input } from '@chakra-ui/react'
import {
  LuUserSearch,
  LuSearch,
  LuRefreshCw,
  LuTriangleAlert,
  LuFolderOpen,
  LuPackage,
} from 'react-icons/lu'

import { salesHistoryService } from '../../../api/services/salesHistory.service'
import type {
  SalesHistorySupplierMatch,
  SupplierSalesHistoryResponse,
  SalesComplianceStatus,
  SalesPaymentStatus,
  SalesHistoryGrade,
} from '../../../api/models/response/salesHistory'

const COMPLIANCE_INFO: Record<SalesComplianceStatus, { label: string; color: string; dot: string }> = {
  Vigente: { label: 'Vigente', color: '#1d6664', dot: '🟢' },
  Alerta: { label: 'Alerta', color: '#a8740e', dot: '🟡' },
  Rechazado: { label: 'Rechazado', color: '#b23a26', dot: '🔴' },
  Desconocido: { label: 'Sin datos', color: '#857b71', dot: '⚪' },
}

function fmt(value: number | null | undefined, digits = 2): string {
  if (value == null) return '—'
  return value.toLocaleString('es-PE', { maximumFractionDigits: digits })
}

function fmtUsd(value: number | null | undefined): string {
  if (value == null) return '—'
  return `$ ${value.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('es-PE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function fmtGrades(grades: SalesHistoryGrade[]): string {
  if (!grades.length) return '—'
  return grades.map((g) => `${g.mineralName} ${fmt(g.grade, 3)}${g.unit ? ` ${g.unit}` : ''}`).join(' · ')
}

export default function HistorialVentasPage() {
  const [search, setSearch] = useState('')
  const [matches, setMatches] = useState<SalesHistorySupplierMatch[]>([])
  const [searching, setSearching] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const [data, setData] = useState<SupplierSalesHistoryResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Búsqueda de proveedores (debounce manual de 350 ms).
  useEffect(() => {
    let active = true
    setSearching(true)
    const t = setTimeout(() => {
      void salesHistoryService
        .searchSuppliers(search.trim() || undefined)
        .then((res) => {
          if (active) setMatches(res)
        })
        .catch(() => {
          if (active) setMatches([])
        })
        .finally(() => {
          if (active) setSearching(false)
        })
    }, 350)
    return () => {
      active = false
      clearTimeout(t)
    }
  }, [search])

  const loadSupplier = useCallback(async (supplierId: string) => {
    setSelectedId(supplierId)
    setLoading(true)
    setError(null)
    try {
      const res = await salesHistoryService.getSupplierHistory(supplierId)
      setData(res)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      {/* Header */}
      <Box>
        <Flex alignItems="center" gap={2}>
          <LuUserSearch size={22} color="#b4532a" />
          <Text fontSize="2xl" fontWeight="800">Historial de Ventas — Vista 360° del Proveedor</Text>
        </Flex>
        <Text fontSize="sm" color="#857b71">
          Consolidación forense post-operativa: agrega los lotes con liquidación financiera completada de un proveedor (RUC o DNI), su perfil comercial, su semáforo de cumplimiento (REINFO/SUNAT) y la matriz histórica de transacciones.
        </Text>
      </Box>

      {/* Buscador */}
      <Box border="1px solid #e8dfd1" borderRadius="xl" p={4}>
        <Flex alignItems="center" gap={2} mb={3}>
          <LuSearch size={16} color="#857b71" />
          <Input
            size="sm"
            placeholder="Buscar por RUC, DNI o razón social…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            maxW="420px"
          />
          {searching && <Spinner size="xs" color="#b5aa9d" />}
        </Flex>

        {matches.length === 0 && !searching && (
          <Text fontSize="xs" color="#b5aa9d">No hay proveedores con ventas liquidadas que coincidan.</Text>
        )}

        {matches.length > 0 && (
          <Flex direction="column" gap={1}>
            {matches.map((m) => {
              const active = m.supplierId === selectedId
              return (
                <Flex
                  key={m.supplierId}
                  as="button"
                  alignItems="center"
                  justifyContent="space-between"
                  px={3}
                  py={2}
                  borderRadius="md"
                  border="1px solid"
                  borderColor={active ? '#b4532a' : '#e8dfd1'}
                  bg={active ? '#eef7f6' : '#FFFFFF'}
                  cursor="pointer"
                  _hover={{ bg: active ? '#eef7f6' : '#faf6f0' }}
                  onClick={() => void loadSupplier(m.supplierId)}
                >
                  <Box textAlign="left" minW={0}>
                    <Text fontSize="sm" fontWeight="700" color="#2a2622" truncate>{m.legalName}</Text>
                    <Text fontSize="11px" color="#857b71">
                      RUC {m.ruc ?? '—'}{m.commercialName ? ` · ${m.commercialName}` : ''}
                    </Text>
                  </Box>
                  <Flex direction="column" alignItems="flex-end" flexShrink={0} pl={3}>
                    <Text fontSize="sm" fontWeight="800" color="#b4532a">{m.liquidatedLots}</Text>
                    <Text fontSize="10px" color="#b5aa9d">lotes · {fmtDate(m.lastSettlementDate)}</Text>
                  </Flex>
                </Flex>
              )
            })}
          </Flex>
        )}
      </Box>

      {error && <Box bg="#fae1db" color="#991b1b" p={3} borderRadius="md" fontSize="sm">{error}</Box>}

      {loading && (
        <Flex justifyContent="center" alignItems="center" py={16} gap={2}>
          <Spinner size="sm" /><Text fontSize="sm" color="#857b71">Cargando perfil del proveedor…</Text>
        </Flex>
      )}

      {!loading && data && <SupplierProfile data={data} onRefresh={() => void loadSupplier(data.supplier.supplierId)} />}

      {!loading && !data && !error && (
        <Flex justifyContent="center" alignItems="center" py={16}>
          <Text fontSize="sm" color="#b5aa9d">Selecciona un proveedor para ver su Vista 360°.</Text>
        </Flex>
      )}
    </Box>
  )
}

/* ── Perfil 360° del proveedor ── */
function SupplierProfile({ data, onRefresh }: { data: SupplierSalesHistoryResponse; onRefresh: () => void }) {
  const { supplier, kpis, compliance } = data
  return (
    <Box display="flex" flexDirection="column" gap={4}>
      {/* Identidad + semáforo */}
      <Box border="1px solid #e8dfd1" borderRadius="xl" p={4}>
        <Flex justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={3}>
          <Box minW={0}>
            <Text fontSize="lg" fontWeight="800" color="#2a2622">{supplier.legalName}</Text>
            <Text fontSize="sm" color="#857b71">
              RUC {supplier.ruc ?? '—'}{supplier.commercialName ? ` · ${supplier.commercialName}` : ''}
            </Text>
            {supplier.taxAddress && <Text fontSize="xs" color="#b5aa9d">{supplier.taxAddress}</Text>}
          </Box>
          <ComplianceBadge compliance={compliance} />
        </Flex>
      </Box>

      <Flex gap={2} flexWrap="wrap" justifyContent="flex-end">
        <ActionButton icon={<LuRefreshCw size={16} />} label="Refrescar" onClick={onRefresh} />
      </Flex>

      {/* KPIs */}
      <Flex gap={3} flexWrap="wrap">
        <Kpi
          label="Volumen Total Entregado"
          value={`${fmt(kpis.totalNetWeightTmn, 3)} TMN`}
          color="#b4532a"
          hint="Σ toneladas métricas netas secas"
        />
        <Kpi label="Lotes liquidados" value={String(kpis.totalLots)} color="#2a2622" />
        <Kpi label="Valorización acumulada" value={fmtUsd(kpis.totalValuationUsd)} color="#2a2622" />
        <Kpi
          label="Frecuencia de Venta"
          value={kpis.avgSaleFrequencyDays != null ? `${fmt(kpis.avgSaleFrequencyDays, 1)} d` : '—'}
          color={kpis.overFrequencyAlert ? '#b23a26' : '#2a2622'}
          hint={kpis.frequencyProfile}
        />
      </Flex>

      {kpis.overFrequencyAlert && (
        <Flex bg="#fcefeb" border="1px solid #fecaca" borderRadius="md" p={3} gap={2} alignItems="center">
          <LuTriangleAlert size={16} color="#b23a26" />
          <Text fontSize="sm" color="#991b1b">
            Alerta de Sobrefrecuencia: entregas cada ≤ 2 días. Posible acopio de mineral de múltiples mineros informales no registrados.
          </Text>
        </Flex>
      )}

      {/* Ley promedio ponderada */}
      <Box border="1px solid #e8dfd1" borderRadius="xl" p={4}>
        <Text fontSize="sm" fontWeight="700" mb={1}>Ley Promedio Histórica (ponderada por volumen)</Text>
        {kpis.averageGrades.length === 0 ? (
          <Text fontSize="xs" color="#b5aa9d">Sin leyes registradas en las liquidaciones.</Text>
        ) : (
          <Flex gap={5} flexWrap="wrap" mt={2}>
            {kpis.averageGrades.map((g) => (
              <Flex key={g.mineralName} direction="column" gap="2px" minW="120px">
                <Text fontSize="11px" color="#857b71" fontWeight="600">{g.mineralName}</Text>
                <Text fontSize="lg" color="#2a2622" fontWeight="800" lineHeight="1.1">
                  {fmt(g.grade, 3)} <Text as="span" fontSize="xs" color="#b5aa9d">{g.unit ?? ''}</Text>
                </Text>
              </Flex>
            ))}
          </Flex>
        )}
      </Box>

      {/* Grilla de transacciones */}
      <TransactionsTable data={data} />
    </Box>
  )
}

/* ── Semáforo de cumplimiento ── */
function ComplianceBadge({ compliance }: { compliance: SupplierSalesHistoryResponse['compliance'] }) {
  const info = COMPLIANCE_INFO[compliance.status]
  return (
    <Box bg={info.color} borderRadius="xl" px={4} py={3} minW="240px">
      <Flex alignItems="center" gap={2}>
        <Text fontSize="xl">{info.dot}</Text>
        <Box>
          <Text fontSize="11px" color="#FFFFFFCC" fontWeight="600" textTransform="uppercase" letterSpacing="0.04em">
            Estado de Cumplimiento Actual
          </Text>
          <Text fontSize="lg" fontWeight="800" color="#FFFFFF" lineHeight="1.1">{info.label}</Text>
        </Box>
      </Flex>
      <Box mt={2}>
        <Text fontSize="10px" color="#FFFFFFDD">
          REINFO: {compliance.reinfoStatus ?? '—'} · RUC: {compliance.sunatTaxpayerStatus ?? '—'}/{compliance.sunatTaxpayerCondition ?? '—'}
        </Text>
        {compliance.reasons.slice(0, 2).map((r, i) => (
          <Text key={i} fontSize="10px" color="#FFFFFFCC">• {r}</Text>
        ))}
      </Box>
    </Box>
  )
}

/* ── Listado Detallado de Transacciones (lotes liquidados) ── */
function TransactionsTable({ data }: { data: SupplierSalesHistoryResponse }) {
  const navigate = useNavigate()
  if (data.transactions.length === 0) {
    return (
      <Box border="1px solid #e8dfd1" borderRadius="xl" p={6}>
        <Text fontSize="sm" color="#b5aa9d" textAlign="center">Este proveedor aún no tiene lotes liquidados.</Text>
      </Box>
    )
  }
  return (
    <Box border="1px solid #e8dfd1" borderRadius="xl" overflow="hidden">
      <Flex bg="#faf6f0" px={4} py={2.5} alignItems="center" gap={2}>
        <Text fontSize="sm" fontWeight="700">Listado Detallado de Transacciones</Text>
        <Text fontSize="xs" color="#b5aa9d">— lotes liquidados (foto histórica inmutable)</Text>
      </Flex>
      <Box overflowX="auto">
        <Box minW="900px">
          <Flex bg="#faf6f0" px={4} py={2} fontSize="10px" color="#857b71" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" borderTop="1px solid #efe8dd">
            <Box w="130px" flexShrink={0}>Lote</Box>
            <Box w="130px" flexShrink={0}>Verificación</Box>
            <Box w="130px" flexShrink={0}>Recepción</Box>
            <Box w="110px" flexShrink={0} textAlign="right">Peso Neto</Box>
            <Box flex={1} minW="160px">Leyes Finales</Box>
            <Box w="120px" flexShrink={0} textAlign="right">Valorización</Box>
            <Box w="100px" flexShrink={0} textAlign="center">Pago</Box>
            <Box w="80px" flexShrink={0} textAlign="center">Trazab.</Box>
          </Flex>
          {data.transactions.map((tx) => (
            <Flex key={tx.shipmentRecordId} px={4} py={2.5} alignItems="center" borderTop="1px solid #efe8dd" fontSize="xs">
              <Box w="130px" flexShrink={0}>
                <Text
                  fontSize="sm"
                  fontWeight="700"
                  color="#b4532a"
                  cursor="pointer"
                  _hover={{ textDecoration: 'underline' }}
                  onClick={() => navigate(`/app/traslados/${tx.shipmentRecordId}`)}
                  truncate
                >
                  {tx.batchCode}
                </Text>
              </Box>
              <Box w="130px" flexShrink={0}><Text color="#4f4842">{fmtDateTime(tx.verifiedAt)}</Text></Box>
              <Box w="130px" flexShrink={0}><Text color="#4f4842">{fmtDateTime(tx.receivedAt)}</Text></Box>
              <Box w="110px" flexShrink={0} textAlign="right">
                <Text fontWeight="700" color="#2a2622">{tx.netWeightTmn != null ? `${fmt(tx.netWeightTmn, 3)}` : '—'}</Text>
              </Box>
              <Box flex={1} minW="160px"><Text color="#4f4842">{fmtGrades(tx.grades)}</Text></Box>
              <Box w="120px" flexShrink={0} textAlign="right"><Text fontWeight="700" color="#2a2622">{fmtUsd(tx.valuationUsd)}</Text></Box>
              <Box w="100px" flexShrink={0} textAlign="center"><PaymentBadge status={tx.paymentStatus} /></Box>
              <Box w="80px" flexShrink={0} textAlign="center">
                <Flex gap={2} justifyContent="center">
                  <Box
                    as="button"
                    title="Ver trazabilidad del lote"
                    cursor="pointer"
                    color="#857b71"
                    _hover={{ color: '#b4532a' }}
                    onClick={() => navigate(`/app/traslados/${tx.shipmentRecordId}`)}
                  >
                    <LuPackage size={15} />
                  </Box>
                  {tx.batchExpedienteCode && (
                    <Box
                      as="button"
                      title="Expediente de traslado (foto histórica)"
                      cursor="pointer"
                      color="#857b71"
                      _hover={{ color: '#b4532a' }}
                      onClick={() => navigate(`/app/identidad/expedientes/${tx.batchExpedienteCode}`)}
                    >
                      <LuFolderOpen size={15} />
                    </Box>
                  )}
                </Flex>
              </Box>
            </Flex>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

/* ── Auxiliares ── */
function Kpi({ label, value, color, hint }: { label: string; value: string; color: string; hint?: string }) {
  return (
    <Flex direction="column" px={4} py={3} borderRadius="xl" border="1px solid #e8dfd1" minW="190px" flex={1}>
      <Text fontSize="11px" color="#857b71" fontWeight="600">{label}</Text>
      <Text fontSize="xl" fontWeight="800" color={color} lineHeight="1.1">{value}</Text>
      {hint && <Text fontSize="10px" color="#b5aa9d">{hint}</Text>}
    </Flex>
  )
}

function PaymentBadge({ status }: { status: SalesPaymentStatus }) {
  const map: Record<SalesPaymentStatus, { bg: string; color: string }> = {
    Liquidado: { bg: '#dcefed', color: '#1d6664' },
    Pendiente: { bg: '#f9eccf', color: '#a8740e' },
  }
  const s = map[status]
  return (
    <Box px={1.5} py={0.5} borderRadius="sm" bg={s.bg} display="inline-block">
      <Text fontSize="9px" fontWeight="700" color={s.color}>{status.toUpperCase()}</Text>
    </Box>
  )
}

function ActionButton({
  icon,
  label,
  onClick,
  loading,
}: {
  icon?: React.ReactNode
  label: string
  onClick: () => void
  loading?: boolean
}) {
  return (
    <Flex
      as="button"
      alignItems="center"
      gap={2}
      px={4}
      py={2}
      borderRadius="md"
      bg="transparent"
      color="#b4532a"
      border="1px solid #b4532a"
      fontSize="sm"
      fontWeight="600"
      cursor={loading ? 'wait' : 'pointer'}
      opacity={loading ? 0.7 : 1}
      pointerEvents={loading ? 'none' : 'auto'}
      _hover={{ opacity: loading ? 0.7 : 0.9 }}
      onClick={onClick}
    >
      {icon && (
        <Box as="span" display="inline-flex" style={{ animation: loading ? 'spin 1s linear infinite' : undefined }}>
          {icon}
        </Box>
      )}
      {label}
    </Flex>
  )
}
